import { mix } from 'polished'
import { colors } from 'component-library/__styling/settings/colors'
import { SocialLinks } from 'component-library/navigation/SocialLinks/SocialLinks'
import { SevenNewsSections } from 'publication-types'
import { BaseSectionValues } from '.'

export type SevenNewsSection = keyof typeof SevenNewsSections

interface Colors {
    primaryColor: string
    secondaryColor?: string
}

interface MobileNavColors extends Colors {
    primaryColor: string
    textColor: string
    focusColor: string
    hamburgerColor: string
    chevronColor: string
}

interface DesktopNavColors extends Colors {
    primaryColor: string
    textColor: string
}

interface ColorsWithHover extends Colors {
    hoverColor: string
}

interface CardsColors {
    kicker: ColorsWithHover
    headline: ColorsWithHover
    list: ColorsWithHover
    timestamp: ColorsWithHover
}

interface LocationSelectorColors {
    headerBackground: string
    dropdownBackground: string
    hoverColor: string
    focusColor: string
    selectedColor: string
    textColor: string
}

export interface SevenNewsSectionValues extends BaseSectionValues {
    focusColor: string
    globalNav: Colors
    mobileNav: MobileNavColors
    desktopNav: DesktopNavColors
    locationSelector: LocationSelectorColors
    sectionHeader: { element: ColorsWithHover; text: ColorsWithHover }
    button: ColorsWithHover
    video: Colors
    breadCrumb: ColorsWithHover
    cards: CardsColors
    tag: Colors
    socialLinks?: SocialLinks
    title: string
}

export type SevenNewsSectionProps = { [section in SevenNewsSection]: SevenNewsSectionValues }

export const sevenNewsSectionDefinitions: SevenNewsSectionProps = {
    default: {
        primaryColor: colors.sevennews.toreaBay,
        focusColor: colors.sevennews.blueRibbon,
        globalNav: {
            primaryColor: colors.sevennews.resolutionBlue,
        },
        mobileNav: {
            primaryColor: colors.sevennews.toreaBay,
            hamburgerColor: colors.white,
            textColor: colors.white,
            chevronColor: colors.sevennews.red,
            focusColor: colors.white,
        },
        desktopNav: {
            primaryColor: colors.sevennews.toreaBay,
            textColor: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.resolutionBlue,
            dropdownBackground: colors.sevennews.resolutionBlue,
            hoverColor: mix(0.1, '#fff', colors.sevennews.resolutionBlue),
            focusColor: colors.white,
            selectedColor: colors.sevennews.blueRibbon,
            textColor: colors.white,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.blueRibbon,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.toreaBay,
            },
        },
        button: {
            primaryColor: colors.sevennews.toreaBay,
            hoverColor: colors.sevennews.blueRibbon,
        },
        video: {
            primaryColor: colors.sevennews.blueRibbon,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.resolutionBlue,
            hoverColor: colors.sevennews.blueRibbon,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.toreaBay,
                hoverColor: colors.sevennews.toreaBay,
            },
            timestamp: {
                primaryColor: colors.sevennews.toreaBay,
                hoverColor: colors.sevennews.toreaBay,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.toreaBay,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.toreaBay,
            },
        },
        tag: {
            primaryColor: colors.sevennews.resolutionBlue,
        },

        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
    sunrise: {
        primaryColor: colors.sevennews.piper,
        focusColor: colors.sevennews.blazeOrange,
        globalNav: {
            primaryColor: colors.sevennews.piper,
        },
        mobileNav: {
            primaryColor: colors.white,
            hamburgerColor: colors.white,
            textColor: colors.sevennews.charade,
            chevronColor: colors.sevennews.piper,
            focusColor: colors.sevennews.piper,
        },
        desktopNav: {
            primaryColor: colors.sevennews.piper,
            textColor: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.piper,
            dropdownBackground: colors.white,
            focusColor: colors.sevennews.blazeOrange,
            textColor: colors.sevennews.charade,
            hoverColor: mix(0.1, '#fff', colors.sevennews.alto),
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.piper,
            },
        },
        button: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        video: {
            primaryColor: colors.sevennews.blazeOrange,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.piper,
            },
            timestamp: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
        },
        tag: {
            primaryColor: colors.sevennews.piper,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/Sunrise/',
            twitter: 'https://twitter.com/sunriseon7',
            instagram: 'https://www.instagram.com/sunriseon7/',
            // youtube: 'https://www.youtube.com/user/SunriseOn7',
            email: 'mailto:sunrise@seven.com.au',
        },
        title: 'Sunrise',
    },
    'the-morning-show': {
        primaryColor: colors.sevennews.endeavour,
        focusColor: colors.sevennews.azureRadiance,
        globalNav: {
            primaryColor: colors.sevennews.endeavour,
        },
        mobileNav: {
            primaryColor: colors.white,
            hamburgerColor: colors.white,
            textColor: colors.sevennews.charade,
            chevronColor: colors.sevennews.endeavour,
            focusColor: colors.sevennews.endeavour,
        },
        desktopNav: {
            primaryColor: colors.sevennews.endeavour,
            textColor: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.endeavour,
            dropdownBackground: colors.white,
            focusColor: colors.sevennews.azureRadiance,
            textColor: colors.sevennews.charade,
            hoverColor: mix(0.1, '#fff', colors.sevennews.alto),
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.azureRadiance,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.azureRadiance,
            },
        },
        button: {
            primaryColor: colors.sevennews.endeavour,
            hoverColor: colors.sevennews.azureRadiance,
        },
        video: {
            primaryColor: colors.sevennews.azureRadiance,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.endeavour,
            hoverColor: colors.sevennews.azureRadiance,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.endeavour,
            },
            timestamp: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.endeavour,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.azureRadiance,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.azureRadiance,
            },
        },
        tag: {
            primaryColor: colors.sevennews.endeavour,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/morningshowon7/',
            twitter: 'https://twitter.com/morningshowon7',
            instagram: 'https://www.instagram.com/morningshowon7/',
            // youtube: 'https://www.youtube.com/user/themorningshowaus',
            email: 'mailto:themorningshow@seven.com.au',
        },
        title: 'The Morning Show',
    },
    'the-daily-edition': {
        primaryColor: colors.sevennews.nierBlue,
        focusColor: colors.sevennews.dodgerBlue,
        globalNav: {
            primaryColor: colors.sevennews.nierBlue,
        },
        mobileNav: {
            primaryColor: colors.white,
            hamburgerColor: colors.white,
            textColor: colors.sevennews.charade,
            chevronColor: colors.sevennews.nierBlue,
            focusColor: colors.sevennews.nierBlue,
        },
        desktopNav: {
            primaryColor: colors.sevennews.nierBlue,
            textColor: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.nierBlue,
            dropdownBackground: colors.white,
            focusColor: colors.sevennews.dodgerBlue,
            textColor: colors.sevennews.charade,
            hoverColor: mix(0.1, '#fff', colors.sevennews.alto),
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.nierBlue,
                hoverColor: colors.sevennews.dodgerBlue,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.nierBlue,
            },
        },
        button: {
            primaryColor: colors.sevennews.nierBlue,
            hoverColor: colors.sevennews.dodgerBlue,
        },
        video: {
            primaryColor: colors.sevennews.dodgerBlue,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.nierBlue,
            hoverColor: colors.sevennews.dodgerBlue,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.nierBlue,
                hoverColor: colors.sevennews.nierBlue,
            },
            timestamp: {
                primaryColor: colors.sevennews.nierBlue,
                hoverColor: colors.sevennews.nierBlue,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.nierBlue,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.nierBlue,
            },
        },
        tag: {
            primaryColor: colors.sevennews.nierBlue,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/TheDailyEdition7/',
            twitter: 'https://twitter.com/DailyEditionOn7',
            instagram: 'https://www.instagram.com/dailyeditionon7/',
            email: 'mailto:dailyedition@seven.com.au',
        },
        title: 'The Daily Edition',
    },
    'sunday-night': {
        primaryColor: colors.sevennews.pickledBluewood,
        focusColor: colors.sevennews.kashmirBlue,
        globalNav: {
            primaryColor: colors.sevennews.pickledBluewood,
        },
        mobileNav: {
            primaryColor: colors.sevennews.pickledBluewood,
            hamburgerColor: colors.white,
            textColor: colors.white,
            chevronColor: colors.sevennews.alizarinCrimson,
            focusColor: colors.sevennews.kashmirBlue,
        },
        desktopNav: {
            primaryColor: colors.sevennews.pickledBluewood,
            textColor: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.pickledBluewood,
            dropdownBackground: colors.white,
            focusColor: colors.sevennews.kashmirBlue,
            textColor: colors.sevennews.charade,
            hoverColor: mix(0.1, '#fff', colors.sevennews.alto),
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.pickledBluewood,
                hoverColor: colors.sevennews.charade,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.kashmirBlue,
            },
        },
        button: {
            primaryColor: colors.sevennews.pickledBluewood,
            hoverColor: colors.sevennews.charade,
        },
        video: {
            primaryColor: colors.sevennews.kashmirBlue,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.pickledBluewood,
            hoverColor: colors.sevennews.charade,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.kashmirBlue,
                hoverColor: colors.sevennews.kashmirBlue,
            },
            timestamp: {
                primaryColor: colors.sevennews.stormGrey,
                hoverColor: colors.sevennews.stormGrey,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.kashmirBlue,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.kashmirBlue,
            },
        },

        tag: {
            primaryColor: colors.sevennews.pickledBluewood,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
}
